/// <amd-module name="Build/ApprovalsPanel/ManualValidationPanel" />
import * as React from "react";

import * as Resources from "Build/ApprovalsPanel/Resources";
import * as Utilities from "Build/ApprovalsPanel/Utilities";
import { ApprovalStatus, ApprovalPermissions } from "Build/Client/Contracts/Approval";
import { ApprovalStep, ManualValidationDetails } from "Build/Client/Contracts/DataProvider";

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";

import { Button } from "VSSUI/Button";
import { Panel } from "VSSUI/Panel";
import { PanelSection } from "Build/ApprovalsPanel/ApprovalPanel"
import { Spinner } from "VSSUI/Spinner";
import { Status, StatusSize } from "VSSUI/Status";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { ago } from "VSSUI/Utilities/Date";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { useComponentContext } from "VSS/Platform/Layout";
import { VssPersona } from "VSSUI/VssPersona";

export interface IManualValidationPanelProps {
    manualValidation: ManualValidationDetails;
    stageName: string;
    onBackClick: (event: React.SyntheticEvent<HTMLElement>) => void;
    onSubmitManualValidations: (status: ApprovalStatus, manualValidations: ManualValidationDetails[], message?: string, deferredTo?: Date | null) => void;
    onDismiss: () => void;
}

export const ManualValidationPanel: React.SFC<IManualValidationPanelProps> = props => {

     const manualValidationStatus = Utilities.checksToApprovalStatus(props.manualValidation.status);
     const statusIndicatorData = Utilities.getApprovalStatusIndicatorData(manualValidationStatus, true);

    const onSubmit = (status: ApprovalStatus, message: string) => {
        let manualValidations: ManualValidationDetails[] = new Array<ManualValidationDetails>();
        manualValidations.push(props.manualValidation);
        props.onSubmitManualValidations(status, manualValidations, message);
    }

    let description: string = statusIndicatorData.label;
    if (manualValidationStatus === ApprovalStatus.Pending) {
        const remainingTimeoutForManualValidation = Utilities.getRemainingMinutesForCheckTimeout(props.manualValidation.timeout, props.manualValidation.createdDate);
        description = format(Resources.ApprovalTimeForTimeoutFormat, description, Utilities.getCheckTimeoutOrReevaluationString(Resources.Timeout, remainingTimeoutForManualValidation));
    }
     return (
         <Panel
            backButtonProps={{ onClick: props.onBackClick }}
            contentClassName="no-h-padding"
            description={description}
            lightDismiss={true}
            modal={false}
            onDismiss={props.onDismiss}
            titleProps={{ text: Resources.ManualValidation }}
       >
            <div className="flex-column flex-grow">
                 <div className="flex-column rhythm-vertical-24 v-scroll-auto flex-grow">
                    {props.manualValidation.jobName && (
                        <PanelSection title={Resources.Job}>
                            <span className="manual-validation-jobname">{props.manualValidation.jobName}</span>
                        </PanelSection>
                     )}
                     <PanelSection>
                         <div className="flex-column rhythm-vertical-16">
                            <ManualValidationsSection
                                approvalSteps={props.manualValidation.steps}
                                submitManualValidations={onSubmit}
                                manualValidationStatus={props.manualValidation.status}
                            />
                        </div>
                    </PanelSection>
                    {props.manualValidation.instructions && (
                        <PanelSection title={Resources.InstructionsLabel}>
                            <span className="manual-validation-instructions">{props.manualValidation.instructions}</span>
                        </PanelSection>
                    )}
                </div>
            </div>
         </Panel>
     );
 };

interface IManualValidationsSection {
    approvalSteps: ApprovalStep[];
    manualValidationStatus: ApprovalStatus;
    submitManualValidations: (status: ApprovalStatus, message?: string) => void;
}

const ManualValidationsSection: React.SFC<IManualValidationsSection> = props => {

    let myManualValidations = new ObservableArray<ApprovalStep>([]);
    let approvedManualValidations = new ObservableArray<ApprovalStep>([]);
    let rejectedManualValidations = new ObservableArray<ApprovalStep>([]);
    let timedOutManualValidations = new ObservableArray<ApprovalStep>([]);
    let cancelledManualValidations = new ObservableArray<ApprovalStep>([]);
    let otherManualValidations = new ObservableArray<ApprovalStep>([]);

    const categorizeManualValidations = (approvalSteps: ApprovalStep[]): void => {
         approvalSteps.map((manualValidation) => {
            if (manualValidation.permissions === ApprovalPermissions.Update && manualValidation.status === ApprovalStatus.Pending) {
                myManualValidations.value.push(manualValidation);
            }
            else if (manualValidation.status === ApprovalStatus.Approved) {
                approvedManualValidations.value.push(manualValidation);
            }
            else if (manualValidation.status === ApprovalStatus.Rejected) {
                rejectedManualValidations.value.push(manualValidation);
            }
            else if (manualValidation.status === ApprovalStatus.TimedOut) {
                timedOutManualValidations.value.push(manualValidation);
            }
            else if (manualValidation.status === ApprovalStatus.Canceled) {
                cancelledManualValidations.value.push(manualValidation);
            }
            else if (manualValidation.permissions === ApprovalPermissions.View) {
                otherManualValidations.value.push(manualValidation);
            }
        });
    }

    categorizeManualValidations(props.approvalSteps)

    const onSubmitManualValidations = (status: ApprovalStatus, commentMessage: string) => {
        props.submitManualValidations(status, commentMessage);
    }

    return (
        <div>
            {myManualValidations.length > 0  && props.manualValidationStatus === ApprovalStatus.Pending &&
                <CategorizedManualValidations
                    approvalSteps={myManualValidations.value}
                >
                    <div className="flex-row secondary-text checks-group-header-row">
                        {Resources.Waiting}
                    </div>
                    <ManualValidationOptionsGroup myManualValidationsCount={myManualValidations.value.length} onSubmit={onSubmitManualValidations}/>
                </CategorizedManualValidations>
            }
            {otherManualValidations.value.length > 0  && props.manualValidationStatus === ApprovalStatus.Pending &&
                <CategorizedManualValidations
                    approvalSteps={otherManualValidations.value}
                >
                    <div className="flex-row secondary-text checks-group-header-row">
                        {Resources.Waiting}
                    </div>
                </CategorizedManualValidations>
            }
            {rejectedManualValidations.length > 0  &&
                <CategorizedManualValidations
                    approvalSteps={rejectedManualValidations.value}
                />
            }
            {approvedManualValidations.length > 0  &&
                <CategorizedManualValidations
                    approvalSteps={approvedManualValidations.value}
                />
            }
            {timedOutManualValidations.length > 0  &&
                <CategorizedManualValidations
                    approvalSteps={timedOutManualValidations.value}
                />
            }
            {cancelledManualValidations.length > 0  &&
                <CategorizedManualValidations
                    approvalSteps={cancelledManualValidations.value}
                />
            }
        </div>
    );
}

export interface ICategorizedManualValidationProps {
    approvalSteps: ApprovalStep[];
}

export const CategorizedManualValidations: React.SFC<ICategorizedManualValidationProps> = props => {
    return (
        <div>
            { props.children }
            { props.approvalSteps.map(
                (manualValidation: ApprovalStep) => {
                    return <ManualValidationStepRow
                                approvalStep={manualValidation}
                            />
                })
            }
        </div>
    );
}

export interface IManualValidationOptionsGroupProps {
    myManualValidationsCount: number;
    manualValidationStatus?: ApprovalStatus;
    useUpdatedChecksFormat?: boolean;
    onSubmit: (status: ApprovalStatus, message: string, deferredTo: Date | null, isManualValidation: boolean) => void;
}

export const ManualValidationOptionsGroup: React.SFC<IManualValidationOptionsGroupProps> = props => {
    const manualValdiationComment = new ObservableValue<string>("");
    const [showSpinner, setShowSpinner] = React.useState<boolean>(false);

    const onResumeClick = () => {
        setShowSpinner(true);
        props.onSubmit(ApprovalStatus.Approved, manualValdiationComment.value, null, true);
    }

    const onRejectClick = () => {
        setShowSpinner(true);
        props.onSubmit(ApprovalStatus.Rejected, manualValdiationComment.value, null, true);
    }

    const onCommentChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, val: string) => {
        manualValdiationComment.value = val;
    };

    return(
        <div className="manual-validation-options-group">
            <div className={css("flex-row", props.useUpdatedChecksFormat ? "manual-validation-options" : "manual-validation-options-default")}>
                {props.manualValidationStatus &&
                    <div className="flex-row flex-center">
                        <Status {...Utilities.getApprovalStatusIndicatorData(props.manualValidationStatus, true).statusProps} className="pipelines-user-manual-validation-state" size={StatusSize.m} />

                </div>
                }
                <div className="flex-row flex-center">
                    <TextField
                        className={props.useUpdatedChecksFormat ?
                            (props.myManualValidationsCount > 1 ? "manual-validation-comment-box-multiple" : "manual-validation-comment-box") :
                            (props.myManualValidationsCount > 1 ? "manual-validation-comment-box-width-default" : "manual-validation-comment-box-width-many")}
                        rows={1}
                        disabled={showSpinner}
                        multiline={true}
                        onChange={onCommentChanged}
                        value={manualValdiationComment}
                        placeholder={Resources.Comment}/>
                    {showSpinner ? <Spinner className="pipeline-manual-validation-spinner"/> : <div />}
                </div>
                <div className="manual-validation-reject-button">
                    <Button
                        disabled={showSpinner}
                        text={props.myManualValidationsCount > 1 ? Resources.RejectAll : Resources.Reject}
                        onClick={onRejectClick}/>
                </div>
                <div className="manual-validation-resume-button">
                    <Button
                        disabled={showSpinner}
                        text={props.myManualValidationsCount > 1 ? Resources.ResumeAll : Resources.Resume}
                        onClick={onResumeClick}
                        primary={true}/>
                </div>
            </div>
        </div>
    );
}

interface IManualValidationRowProps {
    approvalStep: ApprovalStep;
}

const ManualValidationStepRow: React.SFC<IManualValidationRowProps> = props => {
    const context = useComponentContext();
    const step = props.approvalStep;
    const statusIndicatorData = Utilities.getApprovalStatusIndicatorData(step.status, true);
    const manualValidationStatusIndicator = Utilities.getApprovalStatus(step.status, true);
    const approver = ((step.status === ApprovalStatus.Approved) || (step.status === ApprovalStatus.Rejected)) ? step.actualApprover : null;
    let displayName = approver && approver.displayName;
    displayName = format(Resources.CompletedBy, displayName);
    let imageUrl = approver != null ? approver.imageUrl : "";
    if (!imageUrl && approver)
    {
        imageUrl = getIdentityImageUrl(context.pageContext, approver);
    }

    return (
        <div className="flex-column">
            <div className="pipelines-user-manual-validation-row flex-row flex-center">
                {!(step.status === ApprovalStatus.Pending) && <Status {...statusIndicatorData.statusProps} className="pipelines-user-manual-validation-state" size={StatusSize.m} />}
                {approver && (
                    <VssPersona isTabStop={true} className="pipelines-user-approval-persona" displayName={approver.displayName} imageUrl={imageUrl} size="small" />
                )}
                <div className={css("flex-column flex-grow", "pipelines-user-manual-validation-text")}>
                    {displayName && <span className="body-m text-ellipsis">{displayName}</span>}
                    {!(step.status === ApprovalStatus.Pending) && <span className="body-s text-ellipsis secondary-text">{format(manualValidationStatusIndicator, ago(step.lastModifiedOn))}</span>}
                </div>
            </div>
            {props.approvalStep.comment && (
                <div className="pipelines-manual-validation-comment-row flex-row">
                    <span className="body-m">{props.approvalStep.comment}</span>
                </div>
            )}
        </div>
    );
};